<template>
  <div class="mortgage-calculator__wrapper bg-light px-3 px-xxl-0 py-3" id="calc-mortgage-loan">
    <div class="row">
      <div class="col-xxl-5 offset-xxl-1">
        <h2>Mortgage Calculator</h2>
      </div>
    </div>
    <div class="row">
      <div class="col-xxl-5 offset-xxl-1">
        <div class="calulator-box__wrapper">
          <div class="calulator-box__inner pe-xxl-5">
            <div class="row align-items-end">
              <div class="col-xxxl-8 col-lg-6">
                <div class="form-group">
                  <label for="Cost">Cost of Property</label>
                  <input type="range"
                         :min="model.MinCost"
                         :max="model.MaxCost + 1"
                         name="Cost"
                         id="Cost"
                         class="form-control"
                         v-model.number="propertyCost" />
                </div>
              </div>
              <div class="col-xxxl-4 col-lg-6">
                <div class="form-group">
                  <label for="CostInput"
                         class="visually-hidden">
                    Cost Input
                  </label>
                  <input type="number"
                         :min="model.MinCost"
                         :max="model.MaxCost + 1"
                         name="CostInput"
                         id="CostInput"
                         class="form-control"
                         v-model.number="propertyCost" />
                </div>
              </div>
            </div>
            <div class="form-group down-payment__group">
              <label for="DownPayment">Down payment</label>
              <div class="input-group">
                <input type="number"
                       aria-label="Down payment number"
                       :max="propertyCost"
                       min="0"
                       class="form-control"
                       name="DownPayment"
                       id="DownPayment"
                       v-model.number="downPayment">
                <label for="DownPaymentPercent"
                       class="visually-hidden">Down payment percent</label>
                <input type="number"
                       aria-label="Down payment percent"
                       step="0.1"
                       name="DownPaymentPercent"
                       id="DownPaymentPercent"
                       class="form-control text-end"
                       v-model.number="downPaymentPercent">
                <span class="input-group-text">%</span>
              </div>
            </div>
            <div class="form-group">
              <label for="Interest">Interest Rate</label>
              <div class="input-group">
                <input type="number"
                       class="form-control text-end mb-0 border-end-0"
                       min="0"
                       max="100"
                       step="0.1"
                       id="Interest"
                       name="Interest"
                       v-model.number="interestRate" />
                <span class="input-group-text">%</span>
              </div>
            </div>
            <div class="form-group">
              <label for="Years">Mortgage Length (in years)</label>
              <input class="form-control"
                     id="Years"
                     name="Years"
                     v-model.number="mortgageLength"
                     maxlength="2" />
            </div>
          </div>
        </div>
      </div>
      <div class="col-xxl-5">
        <div class="calulator-box__wrapper ps-xxl-4">
          <b><p>Totals</p></b>
          <div class="d-flex justify-content-between align-items-end mb-5">
            <p class="my-0 me-5">Amount Financed</p>
            <p class="h3 m-0">{{formatCurrency(amountFinanced)}}</p>
          </div>
          <div class="d-flex justify-content-between align-items-end mb-5">
            <p class="my-0 me-5">Total Payments</p>
            <p class="h3 m-0">{{formatCurrency(totalPayments)}}</p>
          </div>
          <div class="d-flex justify-content-between align-items-end mb-5">
            <p class="my-0 me-5">Total Interest</p>
            <p class="h3 m-0">{{formatCurrency(totalInterest)}}</p>
          </div>
          <div class="d-flex justify-content-between align-items-end mb-5">
            <p class="h4 my-0 me-4">Monthly Payment</p>
            <p class="h2 m-0">{{formatCurrency(monthlyPayment)}}</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
  import { ref, computed, watch } from 'vue';
  import { formatCurrency } from '../helpers/formatting';
  export default {
    props: {
      model: {
        type: Object,
        required: true
      }
    },
    setup(props) {
      let propertyCost = ref(props.model.PropertyCost);
      let downPayment = ref(props.model.DownPayment);
      let mortgageLength = ref(30);
      let interestRate = ref(props.model.InterestRate);
      let downPaymentPercent = ref(downPayment.value / propertyCost.value * 100);

      watch(propertyCost, (newVal) => {
        if (newVal < 1) {
          propertyCost.value = 1;
        } else if (newVal > props.model.MaxCost) {
          propertyCost.value = props.model.MaxCost;
        } else {
          propertyCost.value = newVal;
        }
        setDownpaymentNumber();
      });

      watch(interestRate, (newVal) => {
        if (newVal < 0) {
          interestRate.value = 0;
        } else if (newVal > 100) {
          interestRate.value = 100;
        } else {
          interestRate.value = newVal;
        }
      });

      watch(downPayment, (newVal) => {
        if (newVal < 0) {
          downPayment.value = 0;
        } else if (downPayment.value > propertyCost.value) {
          downPayment.value = propertyCost.value;
        } else {
          downPayment.value = newVal;
        }
      });

      watch(downPaymentPercent, (newVal) => {
        if (newVal < 0) {
          downPaymentPercent.value = 0;
        } else if (newVal > 100) {
          downPaymentPercent.value = 100;
        } else {
          downPaymentPercent.value = newVal;
        }

        setDownpaymentNumber();
      });

      const setDownpaymentNumber = () => {
        downPayment.value = Math.round(
          propertyCost.value * (downPaymentPercent.value / 100)
        );
      };

      const amountFinanced = computed(() => {
        return propertyCost.value - downPayment.value;
      });

      const monthlyPayment = computed(() => {
        if (+mortgageLength.value > 0) {
          if (interestRate.value <= 0) {
            return amountFinanced.value / (mortgageLength.value * 12);
          } else {
            const rate = interestRate.value / 12 / 100;
            const base = 1 + rate;
            const exponent = -1 * mortgageLength.value * 12;

            const interestDivisor = 1 - (Math.pow(base, exponent));
            const payment = rate * amountFinanced.value / (interestDivisor <= 0 ? 1 : interestDivisor);
            return isNaN(payment) ? 0 : payment;
          }
        }
        return amountFinanced.value + totalInterest.value;
      });

      const totalPayments = computed(() => {
        if (+mortgageLength.value > 0) {
          return monthlyPayment.value * mortgageLength.value * 12;
        }
        return amountFinanced.value;
      });

      const totalInterest = computed(() => {
        if (+mortgageLength.value > 0) {
          return totalPayments.value - amountFinanced.value;
        }
        return amountFinanced.value * interestRate.value / 100;
      });

      return {
        propertyCost,
        downPayment,
        mortgageLength,
        interestRate,
        downPaymentPercent,
        amountFinanced,
        monthlyPayment,
        totalPayments,
        totalInterest,
        formatCurrency
      }
    }
  }
</script>
